import { useLiveQuery } from 'dexie-react-hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { projectionDefaultsResponseSchema, type ProjectionDefaultsResponse } from '@dairy/shared';
import { sessionStore } from '@/auth/session';
import { useSession } from '@/auth/useSession';
import { db } from '@/db/db';
import { apiJson } from '@/lib/api';
import { useOnline } from '@/features/reports/common';

export interface CachedProjectionDefaults {
  tenantId: string;
  fetchedAt: string;
  data: ProjectionDefaultsResponse;
}
export function useProjectionDefaults() {
  const { profile } = useSession();
  const tenantId = profile?.tenantId;
  const userId = profile?.userId;
  const online = useOnline();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const controller = useRef<AbortController | null>(null);
  const cached = useLiveQuery(
    async () => {
      const entry = (await db.meta.get('projectionDefaults'))?.value as CachedProjectionDefaults | undefined;
      if (!entry || entry.tenantId !== tenantId || !Number.isFinite(Date.parse(entry.fetchedAt))) return null;
      const parsed = projectionDefaultsResponseSchema.safeParse(entry.data);
      return parsed.success ? { ...entry, data: parsed.data } : null;
    },
    [tenantId],
    null,
  );
  const refresh = useCallback(async () => {
    const captured = sessionStore.get().profile;
    if (
      !navigator.onLine ||
      !captured ||
      captured.role !== 'OWNER' ||
      captured.tenantId !== tenantId ||
      captured.userId !== userId
    )
      return;
    controller.current?.abort();
    const request = new AbortController();
    controller.current = request;
    const sameOwner = () => {
      const active = sessionStore.get();
      return (
        active.status === 'signedIn' &&
        active.profile?.role === 'OWNER' &&
        active.profile.userId === captured.userId &&
        active.profile.tenantId === captured.tenantId
      );
    };
    setLoading(true);
    setError(null);
    try {
      const data = projectionDefaultsResponseSchema.parse(
        await apiJson('/projections/defaults', { signal: request.signal }),
      );
      await db.transaction('rw', db.meta, async () => {
        if (request.signal.aborted || !sameOwner()) return;
        await db.meta.put({
          key: 'projectionDefaults',
          value: { tenantId, fetchedAt: new Date().toISOString(), data },
        });
      });
    } catch (e) {
      if (!request.signal.aborted && sameOwner()) setError(e);
    } finally {
      if (controller.current === request && !request.signal.aborted) setLoading(false);
    }
  }, [tenantId, userId]);
  useEffect(() => {
    if (online) void refresh();
    else setLoading(false);
    return () => controller.current?.abort();
  }, [online, refresh]);
  return { cached, online, loading, error, refresh };
}
